import { FC } from "react";

import { useFormContext } from "react-hook-form";
import { useSelector } from "react-redux";

import { useQuery } from "@tanstack/react-query";

import {
  ReferentialDwellingCategory,
  ReferentialType,
} from "@/_constants/referentialType.ts";
import { SCvalues } from "@/_helpers/commonComponentsTypes";
import Input from "@/common/components/form/Input";
import SelectForm from "@/common/components/form/SelectForm";
import ToggleButtonForm from "@/common/components/form/ToggleButtonForm";
import { Agency, Lessor } from "@/common/interfaces";
import { getLessors } from "@/modules/dwelling/services/index";
import { RootState } from "@/store";

interface Props {
  agency?: Agency;
}
const CharacteristicsPropertyForm: FC<Props> = ({ agency }) => {
  const referentials = useSelector(
    (state: RootState) => state.referentialReducer.referentials
  );
  const { control, watch } = useFormContext();
  const { data: lessors } = useQuery<Lessor[], Error>(["getLessors"], () =>
    getLessors()
  );

  return (
    <div className="bg-white p-4 rounded-lg shadow space-y-4 relative z-10">
      <h3 className="text-2xl font-bold text-primary">
        Caractéristiques du bien
      </h3>

      <div className="flex gap-3 items-end pb-2 whitespace-nowrap">
        <div className="w-auto flex-shrink-0">
          <ToggleButtonForm
            control={control}
            name="isSociety"
            leftToggle="Société"
            rightToggle="Particulier"
            defaultValue={watch("isSociety")}
          />
        </div>
        {!watch("isSociety") && (
          <div className="min-w-[120px]">
            <SelectForm
              name="lessorDesignation"
              control={control}
              label="Désignation :"
              options={referentials[ReferentialType.LESSOR_DESIGNATION].data}
            />
          </div>
        )}
        {agency?.manageErpBillings === true && (
          <div className="min-w-[120px]">
            <Input
              label={
                watch("isSociety") ? "Nom de société :" : "Nom du bailleur :"
              }
              defaultValue=""
              name="lessorName"
              control={control}
              rules={{
                required: `Veuillez renseigner le nom ${
                  watch("isSociety") ? "de la société" : "du bailleur"
                }`,
              }}
            />
          </div>
        )}
        {agency?.manageErpBillings === false && watch("isSociety") && (
          <div className="min-w-[120px]">
            <SelectForm
              name="lessorId"
              control={control}
              label="Nom de société :"
              options={
                lessors?.map((lessor: Lessor) => {
                  return {
                    label: lessor.name || lessor.firstname,
                    value: lessor.id,
                  };
                }) as SCvalues[]
              }
              rules={{
                required: `Veuillez renseigner le nom ${
                  watch("isSociety") ? "de la société" : "du bailleur"
                }`,
              }}
            />
          </div>
        )}
        {!watch("isSociety") && (
          <div className="min-w-[120px] max-w-[200px] flex-grow-1">
            <Input
              label="Prénom du bailleur :"
              name="lessorFirstname"
              defaultValue=""
              control={control}
            />
          </div>
        )}
        <div className="min-w-[120px] flex-grow-1">
          <Input
            label="N° lot de gestion :"
            name="managementLotNumber"
            control={control}
            defaultValue=""
          />
        </div>
        <div className="min-w-[120px] flex-grow-1">
          <Input
            label="N° de copropriété :"
            name="condominiumNumber"
            control={control}
            defaultValue=""
          />
        </div>
        {watch("isSociety") && (
          <div className="min-w-[120px] flex-grow-1">
            <Input
              label="N° fiscal du local :"
              name="taxNumber"
              control={control}
              defaultValue=""
            />
          </div>
        )}
      </div>
      <div className="flex gap-3 items-end pb-2 whitespace-nowrap overflow-y-visible min-h-[80px]">
        <div className="w-auto flex-shrink-0">
          <ToggleButtonForm
            control={control}
            name="isFurnished"
            leftToggle="Meublé"
            rightToggle="Nue"
            defaultValue={watch("isFurnished")}
          />
        </div>
        <div className="min-w-[120px] flex-grow-1">
          <SelectForm
            name="category"
            defaultValue={watch("category")}
            control={control}
            label="Catégorie de bien :"
            options={referentials[ReferentialType.DWELLING_CATEGORY].data}
            rules={{
              required: "Veuillez renseigner une catégorie.",
            }}
          />
        </div>
        {[
          ReferentialDwellingCategory.APARTMENT,
          ReferentialDwellingCategory.HOUSE,
          ReferentialDwellingCategory.PREMISE,
        ].includes(watch("category")?.value) && (
          <div className="min-w-[120px] flex-grow-1">
            <SelectForm
              name="type"
              control={control}
              label="Type de bien :"
              options={
                referentials[
                  watch("category")?.value ===
                  ReferentialDwellingCategory.PREMISE
                    ? ReferentialType.DWELLING_TYPE_PROMISE
                    : ReferentialType.DWELLING_TYPE
                ].data
              }
              rules={{
                required: "Veuillez renseigner un type.",
              }}
            />
          </div>
        )}

        {/* for Immeuble (Building)  */}
        {[ReferentialDwellingCategory.BUILDING].includes(
          watch("category")?.value
        ) && (
          <div className="min-w-[120px] flex-grow-1">
            <Input
              label="Nombre de batiment :"
              name="buildingNumber"
              control={control}
              defaultValue=""
            />
          </div>
        )}

        {[ReferentialDwellingCategory.BUILDING].includes(
          watch("category")?.value
        ) ? (
          <div className="min-w-[120px] flex-grow-1">
            <Input
              label="Nombre de lots :"
              name="batchNumber"
              control={control}
              defaultValue=""
            />
          </div>
        ) : (
          <div className="min-w-[120px] flex-grow-1">
            <Input
              label="Surface :"
              name="area"
              control={control}
              defaultValue=""
            />
          </div>
        )}

        <div className="min-w-[120px] flex-grow-1">
          <Input
            label="N° Ademe (DPE) :"
            name="ademeNumber"
            control={control}
            defaultValue=""
          />
        </div>

        <div className="min-w-[120px] flex-grow-1">
          <SelectForm
            name="constructionPeriod"
            control={control}
            label="Période de construction :"
            options={
              referentials[ReferentialType.DWELLING_CONSTRUCTION_PERIOD].data
            }
            rules={{
              required: "Veuillez renseigner une période de construction.",
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default CharacteristicsPropertyForm;
